import { Layout, UI, Utils } from 'common';

// cherry-pick lodash methods (the right way). DO NOT use import from 'lodash'.
import isString from 'lodash/isString';
import sum from 'lodash/sum';
import values from 'lodash/values';
import React, { useContext, useEffect, useState } from 'react';
import { CancelAccountContext } from './CancelAccount.context';
import {
	CancelQuestionsDropdown1DropdownOptions,
	CancelQuestionsDropdown2,
	CancelQuestionsDropdown3,
	CancelQuestionsDropdown4,
} from './CancelAccount.enum';

const { Session, Util } = Utils;
const {
	WrappedButton,
	WrappedDropdownWithLabel,
	WrappedInputTextareaWithLabel,
} = UI;
const { LayoutContext } = Layout;

const ResponsivePGridDiv = ({ children }) => (
	<div className="cancellation-section p-grid p-nogutter">
		<div className="p-col-12">{children}</div>
	</div>
);

// converts dropdown enums to array of objects
const processDropdownArray = obj => {
	const dropdownArray = Util.convertEnumToObjectArray(obj);
	const shuffleLastValue = true;
	return Util.shuffle(dropdownArray, shuffleLastValue);
};

const buildDropdown2Data = () =>
	processDropdownArray(CancelQuestionsDropdown2).filter(
		record => record.label !== 'Stamps.com'
	);

const dropdown1Data = processDropdownArray(
	CancelQuestionsDropdown1DropdownOptions
);
const dropdown3Data = processDropdownArray(CancelQuestionsDropdown3);
const dropdown4Data = processDropdownArray(CancelQuestionsDropdown4);

const CancellationQuestions = () => {
	const cancelAccountContext = useContext(CancelAccountContext);
	const { isMobile } = useContext(LayoutContext);

	const dropdown2Data = buildDropdown2Data();

	const [Dropdown1Answer, setDropdown1Answer] = useState(
		cancelAccountContext.Dropdown1Answer
	);
	const [Dropdown1Other, setDropdown1Other] = useState(
		cancelAccountContext.Dropdown1Other
	);
	const [Dropdown2Answer, setDropdown2Answer] = useState(
		cancelAccountContext.Dropdown2Answer
	);
	const [Dropdown2Other, setDropdown2Other] = useState(
		cancelAccountContext.Dropdown2Other
	);
	const [Dropdown3Answer, setDropdown3Answer] = useState(
		cancelAccountContext.Dropdown3Answer
	);
	const [Dropdown3Other, setDropdown3Other] = useState(
		cancelAccountContext.Dropdown3Other
	);
	const [Validations, setValidations] = useState({
		// 0 = untouched, -1 = failed, 1 = valid
		Dropdown1Answer: 0,
		Dropdown1Other: 0,
		Dropdown2Answer: 0,
		Dropdown2Other: 0,
		Dropdown3Answer: 0,
		Dropdown3Other: 0,
	});

	useEffect(() => {
		Session.setFormStateDirty(false);
	}, []);

	const onDropdownChange = e => {
		const { name, value } = e.target;

		if (name === 'Dropdown1Answer') {
			setDropdown1Answer(value);
		} else if (name === 'Dropdown2Answer') {
			setDropdown2Answer(value);
		} else if (name === 'Dropdown3Answer') {
			setDropdown3Answer(value);
		}
		setValidations(prevState => ({
			...prevState,
			[name]: value !== '' ? 1 : -1,
		}));

		Session.setFormStateDirty(true);
	};

	const onInputTextChange = e => {
		const { value, maxLength, name } = e.target;
		const message = value.slice(0, maxLength);

		if (name === 'Dropdown1Other') {
			setDropdown1Other(message);
		} else if (name === 'Dropdown2Other') {
			setDropdown2Other(message);
		} else if (name === 'Dropdown3Other') {
			setDropdown3Other(message);
		}
	};

	const onInputTextBlur = e => {
		const { name } = e.target;
		const inputValue = e.target.value;

		setValidations(prevState => ({
			...prevState,
			[name]: inputValue !== '' ? 1 : -1,
		}));
	};

	const onContinueBtnClick = () => {
		const dropdown1Other = isString(Dropdown1Other)
			? Dropdown1Other.trim()
			: '';
		const dropdown2Other = isString(Dropdown2Other)
			? Dropdown2Other.trim()
			: '';
		const dropdown3Other = isString(Dropdown3Other)
			? Dropdown3Other.trim()
			: '';

		const { internalAccountDetails = {} } = Session.getAccountInfo();
		const { customerGroup } = internalAccountDetails;
		const skipAdaptiveCTA = customerGroup === '101' || customerGroup === '102';

		// validate all fields
		const newValidations = {
			// dropdown 1
			Dropdown1Answer: Dropdown1Answer === '' ? -1 : 1,
			// if user selects "Other (Specify)" option, the dropdown 1 reason can't be empty
			Dropdown1Other:
				Dropdown1Answer === 'other' && dropdown1Other === '' ? -1 : 1,

			// dropdown 2
			Dropdown2Answer: Dropdown2Answer === '' ? -1 : 1,
			// if user selects "Other (Specify)" option, the dropdown 2 reason can't be empty
			Dropdown2Other:
				Dropdown2Answer === 'other' && dropdown2Other === '' ? -1 : 1,

			// dropdown 3
			// if user selects either 'Marketplace' or 'Multi-Carrier' option in dropdown 2,
			// the dropdown 3 option can't be empty
			Dropdown3Answer:
				(Dropdown2Answer === 'marketplace' ||
					Dropdown2Answer === 'multiCarrier') &&
				Dropdown3Answer === ''
					? -1
					: 1,
			// if user selects "Other (Specify)" option, the dropdown 3 reason can't be empty
			Dropdown3Other:
				(Dropdown2Answer === 'marketplace' ||
					Dropdown2Answer === 'multiCarrier') &&
				Dropdown3Answer === 'other' &&
				dropdown3Other === ''
					? -1
					: 1,
		};

		// show the validation result
		setValidations(newValidations);

		/*
		 * If all the fields are the valid, as the value for the validated field is one,
		 * the sum will be equal to the length of the validation object
		 */
		const validationValues = values(newValidations);

		if (sum(validationValues) === validationValues.length) {
			cancelAccountContext.saveCancelAccountInfo(
				{
					Dropdown1Answer,
					Dropdown1Other,
					Dropdown2Answer,
					Dropdown2Other,
					Dropdown3Answer,
					Dropdown3Other,
				},
				() => {
					Session.setFormStateDirty(false);

					// do not display AdaptiveCTA if user is on either of the Freemium plans
					if (skipAdaptiveCTA) {
						cancelAccountContext.setStep('Downgrade');
					} else {
						cancelAccountContext.setStep('AdaptiveCTA');
					}
				}
			);
		}
	};

	return (
		<div className="account-cancellation-questions">
			<ResponsivePGridDiv>
				<h1 className="primary-header">A couple of quick questions...</h1>
			</ResponsivePGridDiv>

			<ResponsivePGridDiv>
				<p data-qa="cancel-reason" {...{ dataqa: 'cancel-reason' }}>
					Please let us know your reason for cancelling your Stamps.com account:
				</p>

				<WrappedDropdownWithLabel
					isInputInvalid={Validations.Dropdown1Answer === -1}
					showErrorBelow={isMobile}
					errorMessage="This field is required"
					className="dropdown"
					uiElementConfig={{
						value: Dropdown1Answer,
						options: dropdown1Data,
						optionLabel: 'label',
						optionValue: 'key',
						onChange: onDropdownChange,
						placeholder: 'Please select one',
						dataqa: 'reason',
						name: 'Dropdown1Answer',
					}}
				/>

				{Dropdown1Answer === 'other' && (
					<WrappedInputTextareaWithLabel
						isInputInvalid={Validations.Dropdown1Other === -1}
						showErrorBelow={isMobile}
						errorMessage="This field is required"
						className="textarea"
						uiElementConfig={{
							rows: 5,
							cols: 31,
							maxLength: 200,
							value: Dropdown1Other,
							custom: 'test',
							onBlur: onInputTextBlur,
							onChange: onInputTextChange,
							dataqa: 'reason-other',
							name: 'Dropdown1Other',
						}}
					/>
				)}
			</ResponsivePGridDiv>

			<ResponsivePGridDiv>
				<p>
					Where will you primarily go to handle your future mailing and shipping
					needs?
				</p>
				<WrappedDropdownWithLabel
					isInputInvalid={Validations.Dropdown2Answer === -1}
					showErrorBelow={isMobile}
					errorMessage="This field is required"
					className="dropdown"
					uiElementConfig={{
						value: Dropdown2Answer,
						options: dropdown2Data,
						optionLabel: 'label',
						optionValue: 'key',
						onChange: onDropdownChange,
						placeholder: 'Please select one',
						dataqa: 'future',
						name: 'Dropdown2Answer',
					}}
				/>

				<div className="text-area">
					{Dropdown2Answer === 'other' && (
						<WrappedInputTextareaWithLabel
							isInputInvalid={Validations.Dropdown2Other === -1}
							showErrorBelow={isMobile}
							errorMessage="This field is required"
							className="textarea"
							uiElementConfig={{
								rows: 5,
								cols: 31,
								maxLength: 200,
								value: Dropdown2Other,
								onBlur: onInputTextBlur,
								onChange: onInputTextChange,
								dataqa: 'future-other',
								name: 'Dropdown2Other',
							}}
						/>
					)}
				</div>
			</ResponsivePGridDiv>

			{(Dropdown2Answer === 'marketplace' ||
				Dropdown2Answer === 'multiCarrier') && (
				<ResponsivePGridDiv>
					{Dropdown2Answer === 'marketplace' ? (
						<p>Please specify which marketplace:</p>
					) : (
						<p>Please specify which multi-carrier software:</p>
					)}
					<WrappedDropdownWithLabel
						isInputInvalid={Validations.Dropdown3Answer === -1}
						showErrorBelow={isMobile}
						errorMessage="This field is required"
						className="dropdown"
						uiElementConfig={{
							value: Dropdown3Answer,
							options:
								Dropdown2Answer === 'multiCarrier'
									? dropdown3Data
									: dropdown4Data,
							optionLabel: 'label',
							optionValue: 'key',
							onChange: onDropdownChange,
							placeholder: 'Please select one',
							dataqa: 'market',
							name: 'Dropdown3Answer',
						}}
					/>

					<div className="text-area">
						{Dropdown3Answer === 'other' && (
							<WrappedInputTextareaWithLabel
								isInputInvalid={Validations.Dropdown3Other === -1}
								showErrorBelow={isMobile}
								errorMessage="This field is required"
								className="textarea"
								uiElementConfig={{
									rows: 5,
									cols: 31,
									maxLength: 200,
									value: Dropdown3Other,
									onBlur: onInputTextBlur,
									onChange: onInputTextChange,
									dataqa: 'market-other',
									name: 'Dropdown3Other',
								}}
							/>
						)}
					</div>
				</ResponsivePGridDiv>
			)}

			<ResponsivePGridDiv>
				<WrappedButton
					label="Continue"
					onClick={onContinueBtnClick}
					dataqa="continue-questions"
				/>
			</ResponsivePGridDiv>
		</div>
	);
};

export default CancellationQuestions;
